<template>
  <div class="tab-content content-scroll-sm">
  
    <!-- No materials data -->
    <div v-if="!hasData || !hasMaterials" class="status-msg">
      <span>
        <svgicon name="empty-timeline" width="249" height="176" :original="true" />
        <p>No materials found</p>
      </span>
    </div>

    <!-- Grouped by ship_to / sold_to (ol-track without shipToRefId) -->
    <template v-else-if="isGroupedByDestination">
      <div v-for="(group, idx) in materials" :key="idx" class="material-group">
        <div class="destination-header">
          <span>Ship To: <strong>{{ group.ship_to || '-' }}</strong></span>
          <span v-if="group.sold_to" class="sold-to-tag">Sold To: <strong>{{ group.sold_to }}</strong></span>
        </div>
        <Table :data="formatItems(group.items)" :stripe="true" class="table-striped bordered material-table" aria-describedby="materialData">
          <table-column
            v-for="column in materialColumns"
            :label="column.title"
            :width="column.width"
            :key="column.id">
            <template slot-scope="scope">
              <span v-if="scope.row[column.label]">{{ scope.row[column.label] }}</span>
              <span v-else>-</span>
            </template>
          </table-column>
        </Table>
      </div>
    </template>

    <!-- Flat materials (track-link or ol-track with shipToRefId) -->
    <template v-else>
      <div v-for="(group, idx) in groupedMaterials" :key="idx" class="material-group">
        <div v-if="group.invoiceNumber" class="invoice-header">
          Invoice: <strong>{{ group.invoiceNumber }}</strong>
          <span v-if="group.lrNumber" class="lr-tag">LR: {{ group.lrNumber }}</span>
        </div>
        <Table :data="group.items" :stripe="true" class="table-striped bordered material-table" aria-describedby="materialData">
          <table-column
            v-for="column in materialColumns"
            :label="column.title"
            :width="column.width"
            :key="column.id">
            <template slot-scope="scope">
              <span v-if="scope.row[column.label]">{{ scope.row[column.label] }}</span>
              <span v-else>-</span>
            </template>
          </table-column>
        </Table>
      </div>
    </template>
  </div>
</template>

<script>
import { materialConstants } from '@/constants/trackConstants';

export default {
  name: 'MaterialsTab',
  props: {
    materials: { type: Array, default: () => [] },
    hasMaterials: { type: Boolean, default: true },
  },
  data() {
    return { materialColumns: materialConstants };
  },
  computed: {
    isGroupedByDestination() {
      return this.materials.length > 0 && this.materials[0] && this.materials[0].ship_to !== undefined;
    },
    hasData() {
      if (this.isGroupedByDestination) {
        return this.materials.some(g => g.items && g.items.length);
      }
      return this.formattedMaterials.length > 0;
    },
    formattedMaterials() {
      return (this.materials || []).map(material => {
        let quantity = '';
        if (material.quantity) {
          if (material.quantity.cases) quantity += `${material.quantity.cases} cases `;
          if (material.quantity.pieces) quantity += `${material.quantity.pieces} pieces`;
        }
        return {
          code: material.code,
          quantity: quantity.trim(),
          weight: material.weight ? `${material.weight.value} ${material.weight.unit}` : '-',
          volume: material.volume ? `${material.volume.value} ${material.volume.unit}` : '-',
          reference_id: material.reference_id || material.custom_fields?.custom_field_cust_ref_id || '',
          material_ref_id: material.material_ref_id || '',
          invoice_number: material.invoice_number || material.invoice?.number || '',
          delivery_number: material.delivery_number || material.custom_fields?.custom_field_delivery_number || '',
          invoiceNumber: material.invoice?.number || null,
          lr_number: material.lr_number || null,
        };
      });
    },
    groupedMaterials() {
      const hasInvoice = this.formattedMaterials.some(m => m.invoiceNumber);
      if (!hasInvoice) {
        return [{ invoiceNumber: null, lrNumber: null, items: this.formattedMaterials }];
      }
      const groups = {};
      this.formattedMaterials.forEach(m => {
        const key = m.invoiceNumber || 'No Invoice';
        if (!groups[key]) {
          groups[key] = { invoiceNumber: m.invoiceNumber, lrNumber: m.lr_number, items: [] };
        }
        groups[key].items.push(m);
        if (m.lr_number && !groups[key].lrNumber) groups[key].lrNumber = m.lr_number;
      });
      return Object.values(groups);
    },
  },
  methods: {
    formatItems(items) {
      return (items || []).map(item => {
        let quantity = '';
        if (item.quantity) {
          if (item.quantity.cases) quantity += `${item.quantity.cases} cases `;
          if (item.quantity.pieces) quantity += `${item.quantity.pieces} pieces`;
          if (!quantity && item.quantity.value) quantity = `${item.quantity.value} ${item.quantity.unit || ''}`;
        }
        return {
          code: item.code,
          quantity: quantity.trim(),
          weight: item.weight ? `${item.weight.value} ${item.weight.unit}` : '-',
          volume: item.volume ? `${item.volume.value} ${item.volume.unit}` : '-',
          reference_id: item.reference_id || '',
          material_ref_id: item.material_ref_id || '',
          invoice_number: item.invoice_number || '',
        };
      });
    },
  },
};
</script>

<style scoped>
.material-group:not(:last-child) { margin-bottom: 16px; }
.destination-header {
  font-size: 13px;
  color: #303133;
  padding: 8px 12px;
  background-color: #f5f7fa;
  border: 1px solid #ebeef5;
  border-bottom: none;
  border-radius: 4px 4px 0 0;
  display: flex;
  gap: 20px;
}
.sold-to-tag {
  color: #606266;
}
.invoice-header {
  font-size: 13px;
  color: #303133;
  padding: 8px 0 4px;
  border-bottom: 1px solid #ebeef5;
  margin-bottom: 8px;
}
.lr-tag {
  margin-left: 12px;
  font-size: 12px;
  color: #909399;
}
.material-table { overflow-x: auto; }
</style>
